/* eslint-disable filename-rules/match */
import { useCallback, useEffect, useMemo, useState } from 'react'

import Bugsnag from '@bugsnag/js'
import styled from '@emotion/styled'
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import { border } from '@arcade/web'
import { ProgressCircular, spacing } from '@cash-design-system/react'
import logger from '@cash-web/shared/util-logger'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { Search, SearchResults, StatusBadge } from '@cash-web/support/feature-main'
import { SupportSuperDuperMainTitle } from '@cash-web/support/ui-components'
import { SupportAction, useAnalyticsContext } from '@cash-web/support/utils'
import { supportedRegions } from '@cash-web/support/utils-region-config'
import { CustomerSupportAccessViewNodes, WebViewViewPage } from '@squareup/customer-data-events-web'

import getRecommendedArticles from '../../data/recommendedArticles'
import i18nextConfig, { fallbackLng as defaultLocale } from '../../next-i18next.config'
import HelpContent from '../../src/components/helpContent'
import RegionalisedLink from '../../src/components/RegionalisedLink'
import useCanonicalURL from '../../src/hooks/useCanonicalUrl'
import useSearchQuery from '../../src/hooks/useSearchQuery'

export const getStaticPaths: GetStaticPaths = () => ({
  paths: Object.keys(supportedRegions).map(region => ({ params: { region } })),
  fallback: 'blocking', // required for defaultRegion fallback in next.config.js
})

export const getStaticProps: GetStaticProps = async ({ locale = defaultLocale, params }) => {
  const region = params?.['region']?.toString().toLowerCase()

  if (!region || !Object.keys(supportedRegions).includes(region)) {
    // TODO cameronandrews remove this and rely on removing fallback
    return { notFound: true, revalidate: 3600 }
  }

  if (['default'].includes(locale)) {
    // Don't attempt to prerender the 'default' locale
    return { notFound: true }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'], i18nextConfig)),
    },
  }
}

interface SearchProp {
  initialHash?: string
}

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${spacing.l};
  border-radius: ${border.radius.medium};
`

const ResultsContainer = styled.div`
  border-radius: ${border.radius.medium};
  overflow: hidden;
  margin-top: 45px;
`

const SearchPage: NextPage<SearchProp> = ({ initialHash = '' }) => {
  const { viewToken } = useAnalyticsContext()
  const canonicalUrl = useCanonicalURL('search')
  const [hash, setHash] = useState('')
  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const [query, setQuery] = useState('')

  const getSearchTerm = () => {
    const params = window.location.hash.substring(1).replace(/%20/g, ' ').split('&')
    try {
      return decodeURIComponent(params[0])
    } catch {
      Bugsnag.notify(new Error(`Help: search error, failed decoding: ${window.location.hash}`))
      return params[0]
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchTerm = getSearchTerm()
      setHash(searchTerm ? searchTerm : initialHash)
    }
  }, [initialHash, setHash])

  const {
    data: dataOnPageLoad,
    isLoading: isLoadingOnPageLoad,
    isError: isErrorOnPageLoad,
  } = useSearchQuery({ query: hash, showMoreResults: false })

  const displayTypes = useMemo(() => dataOnPageLoad?.results?.map(() => 'search'), [dataOnPageLoad?.results])

  const nodeTokens = useMemo(() => dataOnPageLoad?.results?.map(item => item.document_id), [dataOnPageLoad?.results])

  const logPageAnalytics = useCallback(() => {
    logger.addBreadcrumb(isErrorOnPageLoad ? SupportAction.SearchFailed : SupportAction.SearchResultsLoaded)
    const totalCount = dataOnPageLoad?.results?.length
    logCdfEvent(WebViewViewPage({ pageName: 'Support Search Page' }))
    logCdfEvent(
      CustomerSupportAccessViewNodes({
        displayTypes: displayTypes?.toString(),
        nodeTokens: nodeTokens?.toString(),
        totalCount: totalCount,
        trigger: 'SEARCH',
        searchStatus: isErrorOnPageLoad ? 'FAILURE' : 'SUCCESS',
        viewToken: viewToken,
        searchText: hash,
      })
    )
  }, [dataOnPageLoad?.results?.length, logCdfEvent, displayTypes, nodeTokens, isErrorOnPageLoad, viewToken, hash])

  useEffect(() => {
    if (typeof window !== 'undefined' && (dataOnPageLoad || isErrorOnPageLoad) && hash) {
      logPageAnalytics()
    }
  }, [dataOnPageLoad, hash, isErrorOnPageLoad, logPageAnalytics])

  const onHashChanged = useCallback(() => {
    const searchTerm = getSearchTerm()
    if (hash !== searchTerm) {
      window.location.reload()
    }
  }, [hash])

  useEffect(() => {
    if (typeof window !== 'undefined' && (dataOnPageLoad || isErrorOnPageLoad)) {
      onHashChanged()
    }

    window?.addEventListener('hashchange', onHashChanged)

    return () => {
      window?.removeEventListener('hashchange', onHashChanged)
    }
  }, [dataOnPageLoad, isErrorOnPageLoad, onHashChanged])

  const { data: searchData, isLoading, isError } = useSearchQuery({ query, showMoreResults: true })

  return (
    <>
      <Head>
        <title>{t('Cash App - Search', { context: 'search page title' })}</title>
        <meta
          name="description"
          content={t(
            'Get help using the Cash App and learn how to send and receive money without a problem using our support.',
            { context: 'search page metadata description' }
          )}
        />
        <link rel="canonical" href={`${canonicalUrl}${typeof window !== 'undefined' ? window.location.hash : ''}`} />
      </Head>
      <HelpContent>
        <section>
          <StatusBadge t={t} />
          {/* TODO: janec cameronandrews update fontWeight to 600 once Cash Sans font is applied to help center */}
          <SupportSuperDuperMainTitle fontWeight={500} t={t} />
          <Search
            showMoreResults
            recommendedArticles={getRecommendedArticles(t)}
            data={searchData}
            isLoading={isLoading}
            isError={isError}
            initialHash={hash}
            onQueryChange={setQuery}
            Link={RegionalisedLink}
            t={t}
          />
          {isLoadingOnPageLoad && (
            <LoadingContainer>
              <div data-testid="loading-icon">
                <ProgressCircular size="large" />
              </div>
            </LoadingContainer>
          )}
          {(isErrorOnPageLoad || dataOnPageLoad) && (
            <ResultsContainer>
              <SearchResults
                results={dataOnPageLoad?.results ?? []}
                query={hash}
                showMoreResults={false}
                isErrored={isErrorOnPageLoad}
                Link={RegionalisedLink}
                t={t}
                fullScreenMode
              />
            </ResultsContainer>
          )}
        </section>
      </HelpContent>
    </>
  )
}

export default SearchPage
